import { StoreItemVirtualRow } from '@wwe-portal/widget/store-revamp/virtual-row/virtual-row';
import { useMemo } from 'react';
import { daysToMs } from '@wwe-portal/utils/date';
import { create } from 'zustand';
import { persist, StorageValue } from 'zustand/middleware';
import { Virtualizer } from '@tanstack/react-virtual';
import { STORAGE_ITEM_STORE_OFFER_VIEWS } from '@wwe-portal/web/utils/constants/keys';
import { safeSetLocalStorage } from '@wwe-portal/web/libs/storage/safe-local-storage';
import { authStore } from './auth-store';
import { useGetCurrentUser } from '../queries/user-info';
import { getEndDate } from '@wwe-portal/web/utils/offer/utils';

interface ViewData {
  inViewOfferIds: { id: string; expiredAt: string }[];
  viewedOfferIds: { id: string; expiredAt: string }[];
}
interface PipStore {
  userViewData: Map<string, ViewData>;
  clearExpiredOffers: () => void;
  viewedOfferIds: (userId: string | undefined) => void;
  getUserViewData: (userId: string) => ViewData | undefined;
  updateViewData: (userId: string, data: ViewData) => void;
  startViewOffers: (
    userId: string,
    offers: { id?: string; endDate?: Date }[]
  ) => void;
  endViewOffers: (userId: string) => void;
  viewOffers: (
    userId: string,
    offers: { id?: string; endDate?: Date }[]
  ) => void;
}

const createDefaultViewData = () => {
  const data: ViewData = {
    inViewOfferIds: [],
    viewedOfferIds: [],
  };
  return data;
};

// defaultExpiry is 30 days
const defaultExpiry = new Date(Date.now() + daysToMs(30)).toISOString();

// Returns a new Map with expired and duplicate offers removed, dropping users
// whose data fully expired so the persisted localStorage blob can shrink over
// time. Run proactively on load via clearExpiredOffers to keep the blob small
// so a quota error is rarely hit in the first place.
const pruneUserViewData = (
  userViewData: Map<string, ViewData>
): Map<string, ViewData> => {
  const now = Date.now();

  const isExpired = (item: { id: string; expiredAt: string }) => {
    const time = new Date(item.expiredAt).getTime();
    // Treat unparseable dates as expired so stale entries get pruned.
    return Number.isNaN(time) || time < now;
  };

  // Keep only non-expired offers, de-duplicated by id (the view actions only
  // push, so the same offer can pile up many times).
  const pruneList = (list: { id: string; expiredAt: string }[]) => {
    const seen = new Set<string>();
    return list.filter((item) => {
      if (isExpired(item) || seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  };

  const nextViewData = new Map<string, ViewData>();
  userViewData.forEach((value, key) => {
    const inViewOfferIds = pruneList(value.inViewOfferIds);
    const viewedOfferIds = pruneList(value.viewedOfferIds);
    if (inViewOfferIds.length === 0 && viewedOfferIds.length === 0) return;
    nextViewData.set(key, { inViewOfferIds, viewedOfferIds });
  });
  return nextViewData;
};

export const usePipStore = create(
  persist<PipStore>(
    (set, get) => ({
      userViewData: new Map<string, ViewData>(),
      clearExpiredOffers: () => {
        set({ userViewData: pruneUserViewData(get().userViewData) });
      },
      viewedOfferIds: (userId) => {
        if (!userId) return [];
        return get().userViewData.get(userId)?.viewedOfferIds ?? [];
      },
      getUserViewData: (userId) => {
        const userViewData = get().userViewData;
        if (!userViewData.has(userId)) {
          userViewData.set(userId, createDefaultViewData());
        }
        const currentUserViews = get().userViewData.get(userId);
        if (!currentUserViews) return;
        return currentUserViews;
      },
      updateViewData: (userId, data) => {
        const userViewData = get().userViewData;
        const nextViewData = new Map(userViewData);
        nextViewData.set(userId, data);
        set({ userViewData: nextViewData });
      },
      startViewOffers: (userId, offers) => {
        const currentUserViews = get().getUserViewData(userId);
        if (!currentUserViews) return;

        const viewedOffers = currentUserViews.inViewOfferIds?.filter((item) =>
          offers.every((offer) => offer.id !== item.id)
        );

        currentUserViews.inViewOfferIds = [];
        for (const offer of offers) {
          if (!offer.id) continue;
          currentUserViews.inViewOfferIds.push({
            id: offer.id,
            expiredAt: offer.endDate?.toISOString() ?? defaultExpiry,
          });
        }
        currentUserViews.viewedOfferIds.push(...viewedOffers);
        get().updateViewData(userId, currentUserViews);
      },
      endViewOffers: (userId) => {
        const viewData = get().getUserViewData(userId);
        if (!viewData) return;
        const nextViewData = {
          viewedOfferIds: [
            ...viewData.viewedOfferIds,
            ...viewData.inViewOfferIds,
          ],
          inViewOfferIds: [],
        };

        get().updateViewData(userId, nextViewData);
      },
      viewOffers: (userId, offers) => {
        const currentUserViews = get().getUserViewData(userId);
        if (!currentUserViews) return;
        for (const offer of offers) {
          if (!offer.id) continue;
          currentUserViews.viewedOfferIds.push({
            id: offer.id,
            expiredAt: offer.endDate?.toISOString() ?? defaultExpiry,
          });
        }
        get().updateViewData(userId, currentUserViews);
      },
    }),
    {
      name: STORAGE_ITEM_STORE_OFFER_VIEWS,
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          const { state } = JSON.parse(str);
          return {
            state: {
              ...state,
              userViewData: new Map(state.userViewData),
            },
          };
        },
        setItem: (name, newValue: StorageValue<PipStore>) => {
          // Serialize the state persist hands us. functions cannot be JSON
          // encoded, and the Map must be turned into entries.
          const serialize = () =>
            JSON.stringify({
              state: {
                ...newValue.state,
                userViewData: Array.from(newValue.state.userViewData.entries()),
              },
            });
          // On quota, safeSetLocalStorage's default recovery evicts the
          // disposable cosmetic blobs (this store's previous value among them)
          // and retries. The blob is kept small proactively by the on-load
          // clearExpiredOffers below, so quota should rarely be hit at all.
          safeSetLocalStorage(name, serialize);
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
      // Prune expired/duplicate offers on load so the blob stays small. Deferred
      // to a microtask: persist fires this synchronously during store creation,
      // before `usePipStore` is assigned, and pruning writes back through
      // setItem — running it now would hit a temporal-dead-zone reference.
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        queueMicrotask(() => state.clearExpiredOffers());
      },
    }
  )
);

export const useViewedOfferIds = () => {
  const currentUser = useGetCurrentUser();
  const userId = currentUser?.userId;
  const userViewData = usePipStore((state) => state.userViewData);

  return useMemo(() => {
    if (!userId) return new Set<string>();
    const viewedOfferIds = userViewData.get(userId)?.viewedOfferIds;
    return new Set<string>(viewedOfferIds?.map((item) => item.id) ?? []);
  }, [userViewData, userId]);
};

export const handleUpdatePipOnVirtualizeChange = (
  virtualizer: Virtualizer<Window, Element>,
  getRowByIndex: (index: number) => StoreItemVirtualRow | undefined
) => {
  const currentUser = authStore.getState().currentUser;
  const userId = currentUser?.userId;
  if (!userId || virtualizer.isScrolling) return;

  const scrollOffset = virtualizer.scrollOffset;
  const endOffset = scrollOffset + window.innerHeight;

  const items = virtualizer.getVirtualItems();
  const currentInViewOffers = items
    .filter(
      (item) =>
        item.start >= scrollOffset && item.start <= endOffset && item.size > 0
    )
    .flatMap(
      (item) =>
        getRowByIndex(item.index)?.offerRow?.offers?.filter(
          (offer) => offer.OffersID
        ) ?? []
    );

  const store = usePipStore.getState();
  store.startViewOffers(
    userId,
    currentInViewOffers.map((offer) => ({
      id: offer.OffersID,
      endDate: getEndDate(offer.EndDate),
    }))
  );
};
